import { getRoomsForReportingUnit } from "building-generation/utils/getShowroomStats";
import type { Building } from "building-types/building/buildingTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { getReportableUnitDesignatorLabelIds } from "doc/utils/labelTools";
import { reportingUnitLabelFromEntityId } from "doc/utils/reportingUnitDocTools";
import { aabb2ContainsVec2, aabb2OverlapArea, aabb2Overlaps } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { polyLine2AABB2, polyLine2Centroid } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";

/**
 * Returns the building room ids that belong to the selected reporting unit, matching the room ids
 * used by the editor render stages (`building.rooms` keys, including the `__ci__` suffix for
 * instanced rooms). Resolves rooms from both direct unit labels (original rooms) and component
 * instances carrying the label, so a component instance contributes all of its rooms.
 *
 * Returns undefined when there is no building, no valid reporting unit is selected, or the unit
 * owns no rooms — in those cases no per-room filtering is applied and the full plan is drawn.
 */
export function getReportingUnitRoomIds(
  building: Building | undefined,
  selectedReportingUnitLabelId: string | undefined,
): Set<string> | undefined {
  if (!building || !selectedReportingUnitLabelId) {
    return undefined;
  }

  const reportableUnitLabelIds = getReportableUnitDesignatorLabelIds(building.doc);
  if (!reportableUnitLabelIds.includes(selectedReportingUnitLabelId)) {
    return undefined;
  }

  const rooms = getRoomsForReportingUnit(building, selectedReportingUnitLabelId);
  return rooms.length > 0 ? new Set(rooms.map((room) => room.id)) : undefined;
}

/**
 * Returns true when any of the given host rooms belongs to the selected reporting unit. Returns
 * true for every room when no unit is selected (`reportingUnitRoomIds` undefined).
 */
export function hostRoomsInReportingUnit(
  reportingUnitRoomIds: ReadonlySet<string> | undefined,
  rooms: ReadonlyArray<{ readonly id: string } | null | undefined>,
): boolean {
  if (!reportingUnitRoomIds) {
    return true;
  }
  return rooms.some((room) => !!room && reportingUnitRoomIds.has(room.id));
}

/**
 * Returns true when an entity handle resolves to the selected reporting unit, or when no unit
 * filter is active (no unit selected or the unit owns no rooms).
 */
export function docEntityHandleInReportingUnit(
  building: Building | undefined,
  selectedReportingUnitLabelId: string | undefined,
  handle: DocEntityHandle | undefined,
): boolean {
  if (!building || !getReportingUnitRoomIds(building, selectedReportingUnitLabelId)) {
    return true;
  }
  const reportableUnitLabelIds = getReportableUnitDesignatorLabelIds(building.doc);
  return reportingUnitLabelFromEntityId(building.doc, reportableUnitLabelIds, handle) === selectedReportingUnitLabelId;
}

/**
 * Bounding boxes of the selected reporting unit's room outlines. Returns undefined when no unit
 * filter is active.
 */
export function reportingUnitRoomRegionBBs(
  building: Building | undefined,
  selectedReportingUnitLabelId: string | undefined,
): AABB2[] | undefined {
  const reportingUnitRoomIds = getReportingUnitRoomIds(building, selectedReportingUnitLabelId);
  if (!building || !reportingUnitRoomIds) {
    return undefined;
  }
  const regionBBs: AABB2[] = [];
  for (const roomId of reportingUnitRoomIds) {
    const room = building.rooms[roomId];
    if (room) {
      regionBBs.push(polyLine2AABB2(room.outline));
    }
  }
  return regionBBs;
}

/**
 * True when a footprint's bounding box lands on one of a unit's regions: the shared rule for
 * attributing foundation geometry spatially. Overlap, not overlap area, so degenerate footprints —
 * a straight stem-wall outline, a vertical foundation face — still count.
 */
export function footprintInReportingUnitRegions(footprint: AABB2, regionBBs: readonly AABB2[]): boolean {
  return regionBBs.some((regionBB) => aabb2Overlaps(footprint, regionBB));
}

export interface ReportingUnitRegions {
  readonly labelId: string;
  readonly regionBBs: readonly AABB2[];
}

/** Room-outline bounding boxes of every reporting unit that owns rooms. */
export function collectReportingUnitRegions(building: Building): ReportingUnitRegions[] {
  const unitRegions: ReportingUnitRegions[] = [];
  for (const labelId of getReportableUnitDesignatorLabelIds(building.doc)) {
    const regionBBs = reportingUnitRoomRegionBBs(building, labelId);
    if (regionBBs?.length) {
      unitRegions.push({ labelId, regionBBs });
    }
  }
  return unitRegions;
}

/** How much of `footprint` one unit's rooms stand on. */
function coveredArea(footprint: AABB2, regionBBs: readonly AABB2[]): number {
  return regionBBs.reduce((total, regionBB) => total + aabb2OverlapArea(footprint, regionBB), 0);
}

/**
 * The exclusive form of `footprintInReportingUnitRegions`, for callers that can name only one unit:
 * the larger overlap wins when a footprint straddles two. Null when it stands on none.
 */
export function attributedReportingUnitLabelId(
  unitRegions: readonly ReportingUnitRegions[],
  footprint: AABB2,
): string | null {
  const standingOn = unitRegions.filter(({ regionBBs }) => footprintInReportingUnitRegions(footprint, regionBBs));
  if (standingOn.length <= 1) {
    return standingOn[0]?.labelId ?? null;
  }

  const byCoverage = standingOn
    .map(({ labelId, regionBBs }) => ({ labelId, area: coveredArea(footprint, regionBBs) }))
    .sort((a, b) => b.area - a.area);
  return byCoverage[0].labelId;
}

/**
 * Filters disjoint slab/foundation polygons to the selected reporting unit: a polygon is kept when
 * it contains one of the unit's rooms (porches are rooms too, so their slabs are covered). Returns
 * the polygons unchanged when no unit filter is active.
 */
export function filterSlabPolygonsToReportingUnit(
  building: Building | undefined,
  selectedReportingUnitLabelId: string | undefined,
  polygons: readonly PolyLine2[],
): PolyLine2[] {
  const reportingUnitRoomIds = getReportingUnitRoomIds(building, selectedReportingUnitLabelId);
  if (!building || !reportingUnitRoomIds) {
    return [...polygons];
  }

  const unitRoomCentroids: number[][] = [];
  for (const roomId of reportingUnitRoomIds) {
    const room = building.rooms[roomId];
    const centroid = room ? polyLine2Centroid(room.outline) : null;
    if (centroid) {
      // polyLine2Centroid returns vec2Result; destructure before the next call overwrites it.
      const [cx, cy] = centroid;
      unitRoomCentroids.push([cx, cy]);
    }
  }

  return polygons.filter((polygon) => {
    const bb = polyLine2AABB2(polygon);
    return unitRoomCentroids.some(([cx, cy]) => aabb2ContainsVec2(bb, cx, cy));
  });
}
